import { c as _c } from "react/compiler-runtime";
/* eslint-disable react/jsx-props-no-spreading */
import type { Icon } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import type React from 'react';
import type { HTMLAttributeAnchorTarget } from 'react';
import { forwardRef } from 'react';
import type { LinkButtonVariant } from './link-button.types';
type IconPosition = 'left' | 'right';
export type LinkButtonProps = {
  'data-testid'?: string;
  ariaLabel?: string;
  children: string;
  className?: string;
  href?: string;
  iconComponent?: Icon;
  iconPosition?: IconPosition;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement> | React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement> | MouseEvent) => void;
  target?: HTMLAttributeAnchorTarget;
  variant?: LinkButtonVariant;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  destructive?: boolean;
};
const smallSizes: LinkButtonVariant[] = ['snippet-2', 'snippet-3'];
const LinkButton = forwardRef<HTMLAnchorElement | HTMLButtonElement, LinkButtonProps>((t0, ref) => {
  const $ = _c(55);
  let Icon;
  let ariaLabel;
  let children;
  let className;
  let destructive;
  let form;
  let href;
  let onClick;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let target;
  if ($[0] !== t0) {
    ({
      ariaLabel,
      children,
      className,
      disabled: t1,
      destructive,
      form,
      href,
      iconComponent: Icon,
      iconPosition: t2,
      onClick,
      target,
      type: t3,
      variant: t4,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = ariaLabel;
    $[3] = children;
    $[4] = className;
    $[5] = destructive;
    $[6] = form;
    $[7] = href;
    $[8] = onClick;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = target;
  } else {
    Icon = $[1];
    ariaLabel = $[2];
    children = $[3];
    className = $[4];
    destructive = $[5];
    form = $[6];
    href = $[7];
    onClick = $[8];
    props = $[9];
    t1 = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    target = $[14];
  }
  const disabled = t1 === undefined ? false : t1;
  const iconPosition = t2 === undefined ? "right" : t2;
  const type = t3 === undefined ? "button" : t3;
  const variant = t4 === undefined ? "label-2" : t4;
  let t5;
  if ($[15] !== Icon) {
    t5 = Icon && <Icon />;
    $[15] = Icon;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const renderedIcon = t5;
  const t6 = destructive && "fp-link-button-destructive";
  const t7 = `text-${variant}`;
  let t8;
  if ($[17] !== variant) {
    t8 = smallSizes.includes(variant) ? "gap-0.5" : "gap-1";
    $[17] = variant;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = disabled ? "cursor-default" : "cursor-pointer";
  let t10;
  if ($[19] !== className || $[20] !== t6 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t10 = cx("fp-link-button fp-custom-appearance", t6, t7, "inline-flex items-center bg-transparent border-none no-underline w-fit whitespace-nowrap align-middle p-0 m-0", t8, t9, className);
    $[19] = className;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = disabled ? undefined : onClick;
  const t12 = href ? "link" : "button";
  let t13;
  if ($[25] !== ariaLabel || $[26] !== disabled || $[27] !== iconPosition || $[28] !== props || $[29] !== t10 || $[30] !== t11 || $[31] !== t12 || $[32] !== variant) {
    t13 = {
      ...props,
      "aria-label": ariaLabel,
      className: t10,
      "data-icon-position": iconPosition,
      "data-variant": variant,
      disabled,
      onClick: t11,
      role: t12
    };
    $[25] = ariaLabel;
    $[26] = disabled;
    $[27] = iconPosition;
    $[28] = props;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = variant;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const sharedProps = t13;
  if (href) {
    const t14 = disabled ? undefined : href;
    const t15 = ref as React.Ref<HTMLAnchorElement>;
    const t16 = target === "_blank" ? "noopener noreferrer" : undefined;
    const t17 = iconPosition === "left" && renderedIcon;
    let t18;
    if ($[34] !== children) {
      t18 = <span className="flex items-center h-full">{children}</span>;
      $[34] = children;
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    const t19 = iconPosition === "right" && renderedIcon;
    let t20;
    if ($[36] !== sharedProps || $[37] !== t14 || $[38] !== t15 || $[39] !== t16 || $[40] !== t17 || $[41] !== t18 || $[42] !== t19 || $[43] !== target) {
      t20 = <a {...sharedProps} href={t14} ref={t15} rel={t16} target={target}>{t17}{t18}{t19}</a>;
      $[36] = sharedProps;
      $[37] = t14;
      $[38] = t15;
      $[39] = t16;
      $[40] = t17;
      $[41] = t18;
      $[42] = t19;
      $[43] = target;
      $[44] = t20;
    } else {
      t20 = $[44];
    }
    return t20;
  }
  const t14 = ref as React.Ref<HTMLButtonElement>;
  const t15 = iconPosition === "left" && renderedIcon;
  let t16;
  if ($[45] !== children) {
    t16 = <span className="flex items-center h-full">{children}</span>;
    $[45] = children;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const t17 = iconPosition === "right" && renderedIcon;
  let t18;
  if ($[47] !== form || $[48] !== sharedProps || $[49] !== t14 || $[50] !== t15 || $[51] !== t16 || $[52] !== t17 || $[53] !== type) {
    t18 = <button {...sharedProps} form={form} ref={t14} type={type}>{t15}{t16}{t17}</button>;
    $[47] = form;
    $[48] = sharedProps;
    $[49] = t14;
    $[50] = t15;
    $[51] = t16;
    $[52] = t17;
    $[53] = type;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  return t18;
});
export default LinkButton;